import React, { createContext, useContext, useMemo, useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { useAppContext } from './AppContext';
import {
  accountsPayable,
  accountsReceivable,
  bankAccounts as bankAccountsSeed,
  cashFlowSeries,
  chartOfAccounts,
  comparativeReports,
  financeKpis,
  fiscalPeriods,
  fixedAssets as fixedAssetsSeed,
  integrationHooks,
  journalEntries as journalEntriesSeed,
  taxCodes
} from '../data/financeMock';

const FinanceContext = createContext(null);

const ROLE_PERMISSIONS = {
  createInvoice: ['finance_admin', 'contador'],
  postInvoice: ['finance_admin', 'contador'],
  schedulePayment: ['tesorero'],
  markPaid: ['tesorero'],
  rejectInvoice: ['contador', 'auditor', 'finance_admin']
};

const currencyRates = {
  CLP: 1,
  USD: 880,
  EUR: 950
};

const statusOrder = [
  'PENDING_POSTING',
  'POSTED',
  'SCHEDULED_PAYMENT',
  'PAID',
  'REJECTED'
];

const generateId = (prefix) => {
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();
  const stamp = Date.now().toString(36).toUpperCase();
  return `${prefix}-${stamp}${random}`;
};

const canPerform = (role, action) => ROLE_PERMISSIONS[action]?.includes(role);

const toCLP = (invoice) => {
  const rate = currencyRates[invoice.currency] || 1;
  return invoice.totalAmount * rate;
};

const buildHistoryEntry = ({ status, userId, note }) => ({
  id: generateId('INVH'),
  status,
  note: note || '',
  userId,
  timestamp: new Date().toISOString()
});

const computeAging = (invoices) => {
  const template = [
    { bucket: 'Al día', amount: 0 },
    { bucket: '1-30 días', amount: 0 },
    { bucket: '31-60 días', amount: 0 },
    { bucket: '61-90 días', amount: 0 },
    { bucket: '+90 días', amount: 0 }
  ];
  const today = new Date();

  invoices.forEach((invoice) => {
    if (!invoice.dueDate) return;
    const due = new Date(invoice.dueDate);
    const diff = differenceInCalendarDays(today, due);
    const amount = toCLP(invoice);

    if (diff <= 0) {
      template[0].amount += amount;
    } else if (diff <= 30) {
      template[1].amount += amount;
    } else if (diff <= 60) {
      template[2].amount += amount;
    } else if (diff <= 90) {
      template[3].amount += amount;
    } else {
      template[4].amount += amount;
    }
  });

  return template;
};

const computeStatusMetrics = (invoices) => {
  const totals = statusOrder.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});
  invoices.forEach((invoice) => {
    totals[invoice.status] = (totals[invoice.status] || 0) + 1;
  });
  const overdue = invoices.filter(
    (invoice) =>
      invoice.status !== 'PAID' &&
      invoice.status !== 'REJECTED' &&
      invoice.dueDate &&
      new Date(invoice.dueDate) < new Date()
  ).length;
  return {
    totals,
    overdue
  };
};

const enrichAccountsPayable = (seed) => {
  const invoices = seed.invoices || [];
  return {
    ...seed,
    invoices,
    aging: computeAging(invoices),
    metrics: computeStatusMetrics(invoices)
  };
};

export const FinanceProvider = ({ children }) => {
  const { markPurchaseOrderInvoiced, markPurchaseOrderPaid } = useAppContext();
  const [kpis, setKpis] = useState(financeKpis);
  const [periods] = useState(fiscalPeriods);
  const [accounts] = useState(chartOfAccounts);
  const [journals, setJournals] = useState(journalEntriesSeed);
  const [apData, setApData] = useState(() => enrichAccountsPayable(accountsPayable));
  const [arData, setArData] = useState(accountsReceivable);
  const [assets, setAssets] = useState(fixedAssetsSeed);
  const [banks, setBanks] = useState(bankAccountsSeed);
  const [cashSeries, setCashSeries] = useState(cashFlowSeries);
  const [taxCatalog, setTaxCatalog] = useState(taxCodes);
  const [comparatives] = useState(comparativeReports);
  const [integrations] = useState(integrationHooks);

  const addJournalEntry = (entry) => {
    setJournals((prev) => [
      {
        ...entry,
        id: entry.id || `JE-${Date.now()}`,
        status: entry.status || 'DRAFT'
      },
      ...prev
    ]);
  };

  const recalcApState = (invoices, prev) => ({
    ...prev,
    invoices,
    aging: computeAging(invoices),
    metrics: computeStatusMetrics(invoices)
  });

  const upsertVendorInvoice = (invoice) => {
    setApData((prev) => {
      const exists = prev.invoices.find((item) => item.id === invoice.id);
      const invoices = exists
        ? prev.invoices.map((item) => (item.id === invoice.id ? invoice : item))
        : [invoice, ...prev.invoices];
      return recalcApState(invoices, prev);
    });
  };

  const createVendorInvoice = (payload, user) => {
    if (!user || !canPerform(user.role, 'createInvoice')) {
      throw new Error('No tienes permisos para crear facturas de proveedor.');
    }
    const newInvoice = {
      id: payload.id || generateId('AP-INV'),
      vendor: payload.vendor,
      vendorId: payload.vendorId || null,
      documentNumber: payload.documentNumber,
      issueDate: payload.issueDate,
      dueDate: payload.dueDate,
      currency: payload.currency || 'CLP',
      totalAmount: Number(payload.totalAmount) || 0,
      status: 'PENDING_POSTING',
      project: payload.project || null,
      poNumber: payload.poNumber || null,
      poAmount: payload.poAmount != null ? Number(payload.poAmount) : null,
      goodsReceipt: payload.goodsReceipt || null,
      postedAt: null,
      postedBy: null,
      paymentScheduledAt: null,
      paidAt: null,
      paidBy: null,
      statusHistory: [
        ...((payload.statusHistory && payload.statusHistory.length > 0)
          ? payload.statusHistory
          : []),
        buildHistoryEntry({
          status: 'PENDING_POSTING',
          userId: user.id,
          note: payload.note || 'Factura ingresada para posteo.'
        })
      ]
    };
    upsertVendorInvoice(newInvoice);
    return newInvoice;
  };

  const updateInvoice = (invoiceId, updater) => {
    let updatedInvoice = null;
    setApData((prev) => {
      const invoices = prev.invoices.map((invoice) => {
        if (invoice.id !== invoiceId) return invoice;
        updatedInvoice = updater(invoice);
        return updatedInvoice;
      });
      return recalcApState(invoices, prev);
    });
    return updatedInvoice;
  };

  const postVendorInvoice = ({ invoiceId, user, note }) => {
    if (!user || !canPerform(user.role, 'postInvoice')) {
      throw new Error('No tienes permisos para postear facturas.');
    }
    const updated = updateInvoice(invoiceId, (invoice) => ({
      ...invoice,
      status: 'POSTED',
      postedAt: new Date().toISOString(),
      postedBy: user.id,
      statusHistory: [
        ...(invoice.statusHistory || []),
        buildHistoryEntry({
          status: 'POSTED',
          userId: user.id,
          note: note || 'Factura posteada en contabilidad.'
        })
      ]
    }));
    if (updated?.poNumber && markPurchaseOrderInvoiced) {
      markPurchaseOrderInvoiced(updated.poNumber, {
        invoiceId: updated.id,
        note
      });
    }
    return updated;
  };

  const scheduleVendorPayment = ({ invoiceId, user, scheduledFor, note }) => {
    if (!user || !canPerform(user.role, 'schedulePayment')) {
      throw new Error('No tienes permisos para programar pagos.');
    }
    return updateInvoice(invoiceId, (invoice) => ({
      ...invoice,
      status: 'SCHEDULED_PAYMENT',
      paymentScheduledAt: scheduledFor || new Date().toISOString(),
      statusHistory: [
        ...(invoice.statusHistory || []),
        buildHistoryEntry({
          status: 'SCHEDULED_PAYMENT',
          userId: user.id,
          note: note || 'Factura añadida a la corrida de pagos.'
        })
      ]
    }));
  };

  const markVendorInvoiceAsPaid = ({ invoiceId, user, paidAt, note }) => {
    if (!user || !canPerform(user.role, 'markPaid')) {
      throw new Error('No tienes permisos para cerrar pagos.');
    }
    const updated = updateInvoice(invoiceId, (invoice) => ({
      ...invoice,
      status: 'PAID',
      paidAt: paidAt || new Date().toISOString(),
      paidBy: user.id,
      statusHistory: [
        ...(invoice.statusHistory || []),
        buildHistoryEntry({
          status: 'PAID',
          userId: user.id,
          note: note || 'Pago confirmado y conciliado.'
        })
      ]
    }));
    if (updated?.poNumber && markPurchaseOrderPaid) {
      markPurchaseOrderPaid(updated.poNumber, {
        paymentReference: updated.id,
        note
      });
    }
    return updated;
  };

  const rejectVendorInvoice = ({ invoiceId, user, note }) => {
    if (!user || !canPerform(user.role, 'rejectInvoice')) {
      throw new Error('No tienes permisos para rechazar facturas.');
    }
    return updateInvoice(invoiceId, (invoice) => ({
      ...invoice,
      status: 'REJECTED',
      statusHistory: [
        ...(invoice.statusHistory || []),
        buildHistoryEntry({
          status: 'REJECTED',
          userId: user.id,
          note: note || 'Factura rechazada.'
        })
      ]
    }));
  };

  const upsertCustomerInvoice = (invoice) => {
    setArData((prev) => {
      const exists = prev.invoices.find((item) => item.id === invoice.id);
      const invoices = exists
        ? prev.invoices.map((item) => (item.id === invoice.id ? invoice : item))
        : [invoice, ...prev.invoices];
      return { ...prev, invoices };
    });
  };

  const registerAsset = (asset) => {
    setAssets((prev) => [{ ...asset, id: asset.id || `FA-${Date.now()}` }, ...prev]);
  };

  const syncCashProjection = (projection) => {
    setCashSeries((prev) => ({
      ...prev,
      inflows: projection.inflows ?? prev.inflows,
      outflows: projection.outflows ?? prev.outflows
    }));
    setKpis((prev) => ({
      ...prev,
      cashOnHand: projection.cashOnHand ?? prev.cashOnHand,
      workingCapital: projection.workingCapital ?? prev.workingCapital
    }));
  };

  const addTaxCode = (code) => {
    setTaxCatalog((prev) => [{ ...code, id: code.id || `TX-${Date.now()}` }, ...prev]);
  };

  const contextValue = useMemo(
    () => ({
      kpis,
      setKpis,
      periods,
      accounts,
      journals,
      addJournalEntry,
      apData,
      canPerformFinanceAction: (role, action) => canPerform(role, action),
      createVendorInvoice,
      postVendorInvoice,
      scheduleVendorPayment,
      markVendorInvoiceAsPaid,
      rejectVendorInvoice,
      upsertVendorInvoice,
      arData,
      upsertCustomerInvoice,
      assets,
      registerAsset,
      banks,
      cashSeries,
      syncCashProjection,
      taxCatalog,
      addTaxCode,
      comparatives,
      integrations
    }),
    [
      kpis,
      periods,
      accounts,
      journals,
      apData,
      arData,
      assets,
      banks,
      cashSeries,
      taxCatalog,
      comparatives,
      integrations
    ]
  );

  return <FinanceContext.Provider value={contextValue}>{children}</FinanceContext.Provider>;
};

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error('useFinance debe utilizarse dentro de FinanceProvider');
  }
  return context;
};
